import DatePicker from "@/components/date-picker";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { TextArea } from "@/components/text-area/TextArea";
import { InputField } from "@/components/input-field/InputField";
import { AutocompleteSelect } from "@/components/select/AutocompleteSelect";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { onlyNumbers } from "@/utils/reg-expressions";
import {
  getOptionsHandler,
  loadOptionsHandler,
} from "@/pages/grain-requests/sell-orders/schema/handler-functions";
import { getUsuariosMobileByCuenta } from "@/services/users.service";
import {
  getMotivos,
  getProcedencias,
} from "@/services/aux-entities.service";
import { MotiveTypes, RequestTypes } from "@/constants/enums";
import { isEmpty } from "@/utils/query-string";
import { useRoles } from "@/hooks/useRoles";
import { fieldVisitCreationSchema } from "../schema/field-visit-creation.schema";
import { createFieldVisitHandler } from "./handler-functions.ts";

type SelectItem = { label: string; value: string };

export const FieldVisitCreationPage = () => {
  const navigate = useNavigate();
  const { fetchPermissions } = useRoles();

  const [isActive, setIsActive] = useState<boolean>(false);

  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: zodResolver(fieldVisitCreationSchema),
  });

  const watchedValues = useWatch({ control });

  const [loadingCreate, setLoadingCreate] = useState<boolean>(false);

  const hasNoFilledValues = useMemo(() => {
    const {
      account,
      procedencia,
      fechaDeseada,
      contacto,
      motivo,
      motivoOtroDetalle,
    } = watchedValues || {};
  
    return (
      isEmpty(account) ||
      isEmpty(procedencia) ||
      isEmpty(fechaDeseada) ||
      isEmpty(contacto) ||
      isEmpty(motivo) ||
      Boolean(isEmpty(motivoOtroDetalle) && motivo?.[0] === '3')
    );
  }, [watchedValues]);
  

  const handleCancel = () => {
    navigate(AppRoutes.FIELD_VISIT);
  };

  const handleAdd = async () => {
    await createFieldVisitHandler({
      navigate,
      fetchPermissions,
      setLoadingCreate,
      watchedValues,
    });
  };

  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadAccountOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions: (v) => getOptionsHandler({ inputValue: v }),
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };

  const watchedAccount = useWatch({ control, name: "account" });

  const [cuentaUsuarios, setCuentaUsuarios] = useState<any[]>([]);
  const [loadingCuentaUsuarios, setLoadingCuentaUsuarios] =
    useState<boolean>(false);

  const fetchUsuariosByCuenta = async (cuenta?: string) => {
    if (!cuenta) return;
    setLoadingCuentaUsuarios(true);
    const response = await getUsuariosMobileByCuenta({ cuenta });
    setCuentaUsuarios(response ?? []);
    setLoadingCuentaUsuarios(false);
  };

  const [procedencias, setProcedencias] = useState<any[]>([]);
  const [loadingProcedencias, setLoadingProcedencias] =
    useState<boolean>(false);

  const fetchProcedencias = async (cuenta?: string) => {
    if (!cuenta) return;
    const nro = Number(cuenta);
    if (!Number.isFinite(nro)) return;

    setLoadingProcedencias(true);
    const response = await getProcedencias({ cuenta: nro });
    setProcedencias(response ?? []);
    setLoadingProcedencias(false);
  };

  const [motivos, setMotivos] = useState<any[]>([]);
  const [loadingMotivos, setLoadingMotivos] = useState<boolean>(false);

  const fetchMotivos = async () => {
    setLoadingMotivos(true);
    const response = await getMotivos({
      idTipoSolicitud: RequestTypes.VisitaCampo,
      tipoMotivo: MotiveTypes.VisitaACampo,
    } as any);
    setMotivos(response ?? []);
    setLoadingMotivos(false);
  };

  useEffect(() => {
    setIsActive(true);
  }, []);

  useEffect(() => {
    if (isActive) fetchMotivos();
  }, [isActive]);

  useEffect(() => {
    if (watchedAccount) {
      setValue("usuarioMobile", undefined);
      setValue("procedencia", []);
      setProcedencias([]);

      fetchUsuariosByCuenta(watchedAccount?.value);
      fetchProcedencias(watchedAccount?.value);
    }
  }, [watchedAccount]);

  const procedenciaItems: SelectItem[] = useMemo(() => {
    return (
      procedencias?.map((p: any) => ({
        label: p?.Nombre ?? p?.nombre ?? "-",
        value: p?.Id ?? p?.id ?? "",
      })) ?? []
    );
  }, [procedencias]);

  const motivoItems: SelectItem[] = useMemo(() => {
    return (
      motivos?.map((m: any) => ({
        label: m?.descripcion ?? m?.Descripcion ?? "-",
        value: String(m?.id ?? m?.Id ?? ""),
      })) ?? []
    );
  }, [motivos]);

  const watchedMotivo = useWatch({ control, name: "motivo" });
  const selectedMotivoId = Array.isArray(watchedMotivo)
    ? Number(watchedMotivo?.[0])
    : undefined;
  const showOtroMotivo = selectedMotivoId === 3;

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar solicitud de visita a campo"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleAdd}
        disableAdd={hasNoFilledValues}
        loading={loadingCreate}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            <GridItem colSpan={1}>
              <Field.Root invalid={!!errors?.account?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px">Cuenta</Text>
                </Field.Label>
                <AsyncAutocompleteSelect
                  control={control}
                  loadOptions={loadAccountOptions}
                  name="account"
                  placeholder="Buscar..."
                  isLoading={loadingAccounts}
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1}>
              <Field.Root invalid={!!errors?.usuarioMobile?.message} gap="12px">
                <Field.Label>
                  <Text fontSize="16px">Usuario Mobile</Text>
                </Field.Label>
                <AutocompleteSelect
                  control={control}
                  inputName="usuarioMobile"
                  loading={loadingCuentaUsuarios}
                  disabled={!watchedAccount || loadingCuentaUsuarios}
                  multiple={false}
                  items={
                    cuentaUsuarios?.map((cu: any) => ({
                      label: `${cu?.nombre} ${cu?.apellido}`,
                      value: cu?.id,
                    })) ?? []
                  }
                />
              </Field.Root>
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <SelectComponent
                fullWidth
                name="procedencia"
                control={control}
                labelText="Establecimiento"
                placeholder="Seleccionar"
                items={procedenciaItems}
                loading={loadingProcedencias}
                disabled={!watchedAccount || loadingProcedencias}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
              />
            </GridItem>

            <GridItem colSpan={1} paddingY="8px">
              <DatePicker
                control={control}
                name="fechaDeseada"
                labelText="Fecha deseada"
                placeholder="Seleccionar"
                minDate={dayjs()}
                maxDate={dayjs().add(30, "day")}
                disablePortal={false}
                minHeight="40px"
              />
            </GridItem>

            <GridItem colSpan={1}>
              <InputField
                inputName="contacto"
                labelText="Telefono de contacto"
                placeholder="Ingresar"
                control={control}
                allowedPattern={onlyNumbers}
                minHeight="40px"
              />
            </GridItem>

            <GridItem colSpan={1} >
              <SelectComponent
                fullWidth
                name="motivo"
                control={control}
                labelText="Motivo"
                placeholder="Seleccionar"
                items={motivoItems}
                loading={loadingMotivos}
                disabled={loadingMotivos}
                handleChange={(value) => {
                  const selectedValue = Array.isArray(value) ? value?.[0] : undefined;
                  const selected = motivoItems.find((m) => m.value === selectedValue);
                  setValue("motivoLabel", selected?.label);
                }}
                closeOnSelect
                clearable
                size="xs"
                minHeight="40px"
              />
            </GridItem>

            {showOtroMotivo && (
              <GridItem colSpan={4}>
                <TextArea
                  errors={errors}
                  inputName="motivoOtroDetalle"
                  register={register}
                  placeholder="Ingresar el motivo de visita"
                  maxLength={50}
                />
              </GridItem>
            )}
          </Grid>
        </Card.Body>
      </Card.Root>

      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Detalle
        </Text>
        <TextArea
          errors={errors}
          inputName="observacionProductor"
          maxLength={50}
          register={register}
          placeholder="Ingresar una descripción de la visita"
          helperText="Máximo 50 caracteres"
        />
      </Flex>
    </Flex>
  );
};
